const isRunningOnClient: boolean = typeof window !== 'undefined' && !!window.document;

const breakpointResolution: number = 100;

/**
 * Build image URL with transformation parameters.
 * Uses Cloudflare CDN for siteassets and Optimizely's image processor for contentassets.
 * @param src - Original image URL
 * @param width - Target width in pixels
 * @param aspectRatio - Optional aspect ratio (width/height) for height calculation
 * @returns Transformed image URL or original src if invalid
 */
export function createSrc(src: string, width: number, aspectRatio?: number): string {
  if (!src || (typeof src === 'string' && !src.trim())) return '';

  if (width <= 0 || !Number.isFinite(width)) {
    // eslint-disable-next-line no-console
    console.warn('Invalid width provided to createSrc:', width);
    return src;
  }

  // Cache lowercase version for performance
  const srcLower = src.toLowerCase();

  // Skip transformation for SVG files
  if (srcLower.endsWith('.svg')) {
    return src;
  }

  // Normalize EPiServer internal CMS paths to public URLs
  if (srcLower.includes('/episerver/cms/content/')) {
    const normalizedSrc = src.replace(/\/EPiServer\/CMS\/Content\//gi, '/').split(',,')[0];
    return createSrc(normalizedSrc, width, aspectRatio);
  }

  // Skip transformation for external URLs (different domains)
  if (srcLower.startsWith('https://')) {
    return src;
  }

  // Keep local mock/public assets as direct URLs so they work in Storybook and local component previews.
  if (srcLower.startsWith('/images/')) {
    return src;
  }

  // Use Cloudflare CDN for all internal paths (siteassets, contentassets, etc.)
  const cleanSrc = src.split('?')[0];

  const options: string[] = [`width=${width}`, 'quality=auto', 'format=auto', 'fit=scale-down'];

  if (aspectRatio && aspectRatio > 0) {
    const height = Math.round(width / aspectRatio);
    options.push(`height=${height}`);
  }

  return `/cdn-cgi/image/${options.join(',')}${cleanSrc}`;
}

function getNewSrc(originalSrc: string, imageNodeWidth: number, currentImageWidth: number = 0): string {
  if (!originalSrc) return '';

  if (imageNodeWidth <= 0 || !Number.isFinite(imageNodeWidth)) {
    // eslint-disable-next-line no-console
    console.warn('Invalid imageNodeWidth provided to getNewSrc:', imageNodeWidth);
    return originalSrc;
  }

  const physicalPixelWidth = isRunningOnClient ? imageNodeWidth * window.devicePixelRatio : imageNodeWidth;

  // Round up to closest 100px for caching efficiency
  const adjustedWidth = Math.ceil(physicalPixelWidth / breakpointResolution) * breakpointResolution;

  const finalWidth = Math.max(adjustedWidth, currentImageWidth);

  return createSrc(originalSrc, finalWidth);
}

export default getNewSrc;
